import {useState, useTransition, type ReactNode} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {
  Clock,
  Zap,
  Plus,
  Pencil,
  Trash2,
  Check,
  AlertCircle,
  Settings2,
  Play,
  Layers,
  MessageSquare,
  MailWarning,
  Power,
  Inbox
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
  NativeSelect,
  NativeSelectOption,
  ScrollArea,
  Spinner,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {DeliveryActionsPanel} from './DeliveryActionsPanel';
import {
  type HopperConfig,
  type HopperTargetType,
  type CreateHopperConfigPayload,
  type UpdateHopperConfigPayload,
  SHEET_COLUMN_OPTIONS,
  DEFAULT_SHEET_COLUMNS,
  hopperConfigsApi
} from '@/api/hopper-configs';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {
  useCreateHopperConfig,
  useUpdateHopperConfig,
  useDeleteHopperConfig,
  useHopperDeliveryActions,
  useClearHopperDeliveryActions,
  useP0SlackAlertConfig,
  useUpdateP0SlackAlertConfig,
  useP0SlackDeliveryActions,
  useClearP0SlackDeliveryActions,
  useRetrySubmissionDelivery,
  useResendDeliveryAction,
  useApolloRelayConfig,
  useUpdateApolloRelayConfig,
  useSubmissionStatusCheckConfig,
  useUpdateSubmissionStatusCheckConfig,
  useP0EscalationConfig,
  useUpdateP0EscalationConfig,
  useSubmissionFieldSample,
  useSheetDeliveryAccount,
  useYoutubeReportReasons,
  useClassificationConfigs
} from '@/api/hooks';
import {isPolicyViolationType} from '@/lib/classificationType';
import {type UpdateSubmissionStatusCheckConfigPayload} from '@/api/submissionStatusCheckConfig';
import {type UpdateP0EscalationConfigPayload} from '@/api/p0EscalationConfig';
import {cn} from '@/lib/utils';
import {showToast} from '@/lib/toast';
import {toneBadge} from '@/lib/status';

export type HopperAdminProps = {
  readonly initialConfigs: HopperConfig[];
  readonly verticals: string[];
};

type FormState = {
  name: string;
  policy_violation: string;
  submission_type: 'Actor' | 'Content';
  mode: 'direct' | 'scheduled';
  schedule: string;
  target_type: HopperTargetType;
  webhook_url: string;
  spreadsheet_id: string;
  sheet_tab: string;
  // Comma-separated SubmissionFieldCatalog keys, in order - parsed with parseSheetColumns on save.
  sheet_columns: string;
  // Opt-in internal-sheet copy, written alongside every delivery whatever target_type is. Columns use
  // the same comma-separated form as sheet_columns.
  internal_sheet_enabled: boolean;
  internal_spreadsheet_id: string;
  internal_sheet_tab: string;
  internal_sheet_columns: string;
  // Pioneer API (youtube) target only. youtube_refresh_token is always blank on load, even when a
  // token is already saved - the server never echoes it back (see HopperConfig.YoutubeRefreshToken).
  // Blank on save means "leave the existing token unchanged"; youtube_refresh_token_was_set is
  // read-only, purely so the form can say "a token is already set" instead of showing nothing.
  youtube_refresh_token: string;
  youtube_refresh_token_was_set: boolean;
  youtube_clear_refresh_token: boolean;
  youtube_reason_id: string;
  youtube_secondary_reason_id: string;
  // Sub-category node name -> secondary reason id. Kept as a map while editing (one row per child of the
  // policy violation) and flattened to a list of pairs on save.
  youtube_secondary_overrides: Record<string, string>;
  youtube_abuse_type: string;
  youtube_comments: string;
  youtube_language: string;
  batch_size: number;
  daily_target: number;
  weekly_target: number;
  monthly_target: number;
  enabled: boolean;
  test_mode: boolean;
  test_simulated_status_code: string;
};

// Typing the daily number seeds the other two, until the admin edits one - after that the value is
// theirs and nothing recomputes it, here or server-side. 30 rather than 30.44 because this is a
// starting suggestion the admin can overwrite, and a round number is easier to reason about.
const WEEKLY_PER_DAILY = 7;
const MONTHLY_PER_DAILY = 30;

function parseSheetColumns(raw: string): string[] {
  return raw
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

// Mirrors SubmissionFieldCatalog.IsLiteral/LiteralValue - a header label shouldn't show the quotes
// that mark a column as free text server-side.
function columnLabel(column: string): string {
  return column.length >= 2 && column.startsWith('"') && column.endsWith('"')
    ? column.slice(1, -1)
    : column;
}

export function blankForm(verticals: string[]): FormState {
  return {
    name: '',
    policy_violation: verticals[0] ?? '',
    submission_type: 'Content',
    mode: 'direct',
    schedule: '',
    target_type: 'webhook',
    webhook_url: '',
    spreadsheet_id: '',
    sheet_tab: '',
    sheet_columns: DEFAULT_SHEET_COLUMNS.join(', '),
    internal_sheet_enabled: false,
    internal_spreadsheet_id: '',
    internal_sheet_tab: '',
    internal_sheet_columns: DEFAULT_SHEET_COLUMNS.join(', '),
    youtube_refresh_token: '',
    youtube_refresh_token_was_set: false,
    youtube_clear_refresh_token: false,
    youtube_reason_id: '',
    youtube_secondary_reason_id: '',
    youtube_secondary_overrides: {},
    youtube_abuse_type: '',
    youtube_comments: '',
    youtube_language: '',
    batch_size: 50,
    daily_target: 0,
    weekly_target: 0,
    monthly_target: 0,
    enabled: true,
    test_mode: false,
    test_simulated_status_code: ''
  };
}

function configToForm(c: HopperConfig): FormState {
  return {
    name: c.name,
    policy_violation: c.policy_violation,
    submission_type: c.submission_type,
    mode: c.schedule ? 'scheduled' : 'direct',
    schedule: c.schedule ?? '',
    target_type: c.target_type,
    webhook_url: c.webhook_url ?? '',
    spreadsheet_id: c.spreadsheet_id ?? '',
    sheet_tab: c.sheet_tab ?? '',
    sheet_columns: (c.sheet_columns.length > 0 ? c.sheet_columns : DEFAULT_SHEET_COLUMNS).join(
      ', '
    ),
    internal_sheet_enabled: c.internal_sheet_enabled,
    internal_spreadsheet_id: c.internal_spreadsheet_id ?? '',
    internal_sheet_tab: c.internal_sheet_tab ?? '',
    internal_sheet_columns: (c.internal_sheet_columns.length > 0
      ? c.internal_sheet_columns
      : DEFAULT_SHEET_COLUMNS
    ).join(', '),
    youtube_refresh_token: '',
    youtube_refresh_token_was_set: c.youtube_refresh_token_set,
    youtube_clear_refresh_token: false,
    youtube_reason_id: c.youtube_reason_id ?? '',
    youtube_secondary_reason_id: c.youtube_secondary_reason_id ?? '',
    youtube_secondary_overrides: Object.fromEntries(
      c.youtube_secondary_reason_overrides.map((o) => [o.sub_category, o.secondary_reason_id])
    ),
    youtube_abuse_type: c.youtube_abuse_type ?? '',
    youtube_comments: c.youtube_comments ?? '',
    youtube_language: c.youtube_language ?? '',
    batch_size: c.batch_size,
    daily_target: c.daily_target,
    weekly_target: c.weekly_target,
    monthly_target: c.monthly_target,
    enabled: c.enabled,
    test_mode: c.test_mode,
    test_simulated_status_code: c.test_simulated_status_code?.toString() ?? ''
  };
}

export function formToPayload(f: FormState): CreateHopperConfigPayload {
  return {
    name: f.name,
    policy_violation: f.policy_violation,
    submission_type: f.submission_type,
    schedule: f.mode === 'scheduled' && f.schedule ? f.schedule : null,
    target_type: f.target_type,
    webhook_url: f.target_type === 'webhook' ? f.webhook_url.trim() || null : null,
    spreadsheet_id: f.target_type === 'sheet' ? f.spreadsheet_id.trim() || null : null,
    sheet_tab: f.target_type === 'sheet' ? f.sheet_tab.trim() || null : null,
    sheet_columns: f.target_type === 'sheet' ? parseSheetColumns(f.sheet_columns) : [],
    internal_sheet_enabled: f.internal_sheet_enabled,
    // Only sent while switched on, so switching it off leaves the stored sheet in place for next time.
    ...(f.internal_sheet_enabled
      ? {
          internal_spreadsheet_id: f.internal_spreadsheet_id.trim(),
          internal_sheet_tab: f.internal_sheet_tab.trim(),
          internal_sheet_columns: parseSheetColumns(f.internal_sheet_columns)
        }
      : {}),
    youtube_refresh_token:
      f.target_type === 'youtube' && f.youtube_refresh_token.trim() !== ''
        ? f.youtube_refresh_token.trim()
        : null,
    youtube_reason_id:
      f.target_type === 'youtube' && f.submission_type === 'Content'
        ? f.youtube_reason_id.trim() || null
        : null,
    // Sent as "" rather than "" || null when non-blank input goes blank - unlike youtube_reason_id/
    // youtube_abuse_type (always required whenever they apply, so blank never needs to mean anything
    // but "unchanged"), this one's genuinely optional, and an analyst clearing a secondary reason they
    // no longer want needs a way to say so distinct from never having touched the field. The server
    // treats null as unchanged and an explicit "" as clearing it - see HopperConfigsController.Update.
    youtube_secondary_reason_id:
      f.target_type === 'youtube' && f.submission_type === 'Content'
        ? f.youtube_secondary_reason_id.trim()
        : null,
    // A full list, not a diff: the server replaces the map wholesale, so a row the analyst blanked is simply
    // absent and an all-blank form sends [] and clears it. Null for anything but a Pioneer Content hopper,
    // so saving a webhook or Actor hopper never touches what is stored.
    youtube_secondary_reason_overrides:
      f.target_type === 'youtube' && f.submission_type === 'Content'
        ? Object.entries(f.youtube_secondary_overrides)
            .map(([sub_category, secondary_reason_id]) => ({
              sub_category,
              secondary_reason_id: secondary_reason_id.trim()
            }))
            .filter((o) => o.secondary_reason_id !== '')
        : null,
    youtube_abuse_type:
      f.target_type === 'youtube' && f.submission_type === 'Actor'
        ? f.youtube_abuse_type.trim() || null
        : null,
    // Sent as "" rather than "" || null, for the same reason as youtube_secondary_reason_id above: both
    // are optional, and on an update null reads as "unchanged", so an analyst who empties the field would
    // otherwise save with the old value still in place and still attached to every report. The server
    // treats a blank string as "clear it".
    youtube_comments: f.target_type === 'youtube' ? f.youtube_comments.trim() : null,
    youtube_language: f.target_type === 'youtube' ? f.youtube_language.trim() : null,
    batch_size: f.batch_size,
    daily_target: f.daily_target,
    weekly_target: f.weekly_target,
    monthly_target: f.monthly_target,
    enabled: f.enabled,
    test_mode: f.test_mode,
    test_simulated_status_code: f.test_simulated_status_code.trim()
      ? Number(f.test_simulated_status_code)
      : null
  };
}

export function HopperModal({
  initial,
  verticals,
  onSave,
  onClose
}: {
  readonly initial: FormState;
  readonly verticals: string[];
  readonly onSave: (f: FormState) => Promise<void>;
  readonly onClose: () => void;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const fieldSample = useSubmissionFieldSample(
    form.policy_violation,
    form.submission_type,
    form.target_type === 'sheet' && form.policy_violation.length > 0
  );
  const youtubeReasons = useYoutubeReportReasons(form.target_type === 'youtube');
  const sheetAccount = useSheetDeliveryAccount(
    form.target_type === 'sheet' || form.internal_sheet_enabled
  );
  const wantsSubCategories = form.target_type === 'youtube' && form.submission_type === 'Content';
  const taxonomy = useClassificationConfigs(wantsSubCategories);

  // The nodes directly under this hopper's policy violation: what a submission's risk is resolved to at
  // delivery time (see SubmissionSubCategory). Ordered as the classification tree orders them.
  const subCategories = (() => {
    const all = taxonomy.data;
    const root = all?.find(
      (c) => isPolicyViolationType(c.type) && c.name === form.policy_violation
    );
    if (!all || !root) {
      return [];
    }

    return all
      .filter((c) => c.parent_id === root.id)
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  })();
  // Saved overrides whose key no longer matches a child - the node was renamed or removed, or the policy
  // violation was changed. Shown rather than dropped: otherwise the mapping silently stops applying and
  // there is nothing on screen to explain why a report went out under the default. Held back until the
  // tree has loaded, or every row would flash as orphaned.
  const orphanedSubCategories =
    taxonomy.data === undefined
      ? []
      : Object.keys(form.youtube_secondary_overrides).filter(
          (key) => !subCategories.some((c) => c.name === key)
        );

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((previous) => ({...previous, [k]: v}));
  }

  function setSubCategoryReason(subCategory: string, reason: string) {
    setForm((previous) => ({
      ...previous,
      youtube_secondary_overrides: {...previous.youtube_secondary_overrides, [subCategory]: reason}
    }));
  }

  function removeSubCategoryReason(subCategory: string) {
    setForm((previous) => ({
      ...previous,
      youtube_secondary_overrides: Object.fromEntries(
        Object.entries(previous.youtube_secondary_overrides).filter(([key]) => key !== subCategory)
      )
    }));
  }

  // Seeding "touched" from the stored value being non-zero IS the whole mechanism: an existing
  // hopper's numbers start touched, so editing Daily never clobbers what the admin set, while a blank
  // form starts at zero and seeds from Daily until the admin takes one over. A deliberate zero is
  // indistinguishable from "never set" here, so editing Daily does re-seed it - the trade for not
  // carrying a per-field dirty flag through save and reload.
  const [weeklyTouched, setWeeklyTouched] = useState(initial.weekly_target > 0);
  const [monthlyTouched, setMonthlyTouched] = useState(initial.monthly_target > 0);

  const [freeTextColumn, setFreeTextColumn] = useState('');

  function addColumn(key: string) {
    const current = parseSheetColumns(form.sheet_columns);
    if (current.includes(key)) {
      return;
    }

    set('sheet_columns', [...current, key].join(', '));
  }

  // Free text is wrapped in double quotes so the server can tell a literal from a field key - see
  // SubmissionFieldCatalog.IsLiteral. Not deduped like addColumn: the legacy delivery this replaces
  // had multiple blank/"PENDING..." columns, and there's no reason to disallow that here.
  function addFreeTextColumn() {
    if (!freeTextColumn) {
      return;
    }

    set(
      'sheet_columns',
      [...parseSheetColumns(form.sheet_columns), `"${freeTextColumn}"`].join(', ')
    );
    setFreeTextColumn('');
  }

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        await onSave(form);
        onClose();
      } catch (error_) {
        setError(getApiErrorMessage(error_, "Couldn't save the delivery config."));
      }
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="text-muted-foreground size-4" />
            {initial.name ? `Edit Delivery: ${initial.name}` : 'New Delivery Configuration'}
          </DialogTitle>
          <DialogDescription>
            Where a policy violation stream&apos;s submissions are delivered, how they are batched,
            and the targets the dashboard measures them against.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-4">
          <FormField label="Configuration Title" htmlFor="hopper-name">
            <Input
              id="hopper-name"
              value={form.name}
              placeholder="e.g. Pioneer - Hate Speech Stream"
              onChange={(e) => {
                set('name', e.target.value);
              }}
            />
          </FormField>

          <FormField label="Policy Violation Stream" htmlFor="hopper-policy-violation">
            <NativeSelect
              id="hopper-policy-violation"
              wrapperClassName="w-full"
              value={form.policy_violation}
              onChange={(e) => {
                set('policy_violation', e.target.value);
              }}>
              {verticals.map((v) => (
                <NativeSelectOption key={v} value={v}>
                  {v}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </FormField>

          <FormField label="Submission Type">
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              className="w-full *:flex-1"
              value={[form.submission_type]}
              onValueChange={(next) => {
                const picked: unknown = next[0];
                if (picked === 'Content' || picked === 'Actor') {
                  set('submission_type', picked);
                }
              }}>
              <ToggleGroupItem value="Content">Content</ToggleGroupItem>
              <ToggleGroupItem value="Actor">Actor</ToggleGroupItem>
            </ToggleGroup>
          </FormField>

          <FormField label="Dispatch Mode">
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              className="w-full *:flex-1"
              value={[form.mode]}
              onValueChange={(next) => {
                const picked: unknown = next[0];
                if (picked === 'direct' || picked === 'scheduled') {
                  set('mode', picked);
                }
              }}>
              <ToggleGroupItem value="direct">
                <Zap /> Direct Send
              </ToggleGroupItem>
              <ToggleGroupItem value="scheduled">
                <Clock /> Scheduled Batch
              </ToggleGroupItem>
            </ToggleGroup>
          </FormField>

          {form.mode === 'scheduled' ? (
            <FormField label="Cron Schedule Expression" htmlFor="hopper-schedule">
              <Input
                id="hopper-schedule"
                className="font-mono"
                value={form.schedule}
                placeholder="0 16 * * * (daily at 16:00 UTC)"
                onChange={(e) => {
                  set('schedule', e.target.value);
                }}
              />
            </FormField>
          ) : null}

          <FormField label="Delivery Target">
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              className="w-full *:flex-1"
              value={[form.target_type]}
              onValueChange={(next) => {
                const picked: unknown = next[0];
                if (picked === 'webhook' || picked === 'sheet' || picked === 'youtube') {
                  set('target_type', picked);
                }
              }}>
              <ToggleGroupItem value="webhook">Webhook</ToggleGroupItem>
              <ToggleGroupItem value="sheet">Google Sheet</ToggleGroupItem>
              <ToggleGroupItem value="youtube">Pioneer API</ToggleGroupItem>
            </ToggleGroup>
          </FormField>

          {form.target_type === 'webhook' ? (
            <FormField label="Webhook Endpoint URL" htmlFor="hopper-webhook-url">
              <Input
                id="hopper-webhook-url"
                className="font-mono"
                value={form.webhook_url}
                placeholder="https://api.external-service.com/webhook"
                onChange={(e) => {
                  set('webhook_url', e.target.value);
                }}
              />
            </FormField>
          ) : form.target_type === 'sheet' ? (
            <>
              <FormField label="Spreadsheet ID" htmlFor="hopper-spreadsheet-id">
                <Input
                  id="hopper-spreadsheet-id"
                  className="font-mono"
                  value={form.spreadsheet_id}
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                  onChange={(e) => {
                    set('spreadsheet_id', e.target.value);
                  }}
                />
                <FieldDescription>
                  Share the sheet with{' '}
                  <code className="bg-muted px-1 py-0.5 break-all">
                    {sheetAccount.data?.email ??
                      'pri-delivery-gateway@<project>.iam.gserviceaccount.com'}
                  </code>{' '}
                  as Editor.
                  {sheetAccount.data?.email ? (
                    <CopyButton
                      variant="link"
                      size="xs"
                      className="text-muted-foreground ml-1 h-auto px-0"
                      value={sheetAccount.data.email}
                    />
                  ) : null}
                </FieldDescription>
              </FormField>

              <FormField label="Sheet Tab (optional)" htmlFor="hopper-sheet-tab">
                <Input
                  id="hopper-sheet-tab"
                  className="font-mono"
                  value={form.sheet_tab}
                  placeholder="Leave blank for the first sheet"
                  onChange={(e) => {
                    set('sheet_tab', e.target.value);
                  }}
                />
              </FormField>

              <FormField label="Columns (in order)" htmlFor="hopper-sheet-columns">
                <Input
                  id="hopper-sheet-columns"
                  className="font-mono"
                  value={form.sheet_columns}
                  list="hopper-sheet-column-options"
                  placeholder="id, content_id, display_name, ..."
                  onChange={(e) => {
                    set('sheet_columns', e.target.value);
                  }}
                />
                <datalist id="hopper-sheet-column-options">
                  {SHEET_COLUMN_OPTIONS.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <div className="flex items-center justify-between">
                  <Button
                    variant="link"
                    size="xs"
                    className="text-primary-ink h-auto px-0"
                    onClick={() => {
                      set('sheet_columns', DEFAULT_SHEET_COLUMNS.join(', '));
                    }}>
                    Reset to CSV export columns
                  </Button>
                  <CopyButton
                    variant="link"
                    size="xs"
                    className="text-muted-foreground h-auto px-0"
                    label="Copy header row"
                    value={() => parseSheetColumns(form.sheet_columns).map(columnLabel).join('\t')}
                  />
                </div>
              </FormField>

              <FormField label="Available Fields">
                {/* The max-height is repeated on the viewport: it is the element that scrolls, and
                    its height is a percentage of the root, which a max-height alone never makes
                    definite. */}
                <ScrollArea className="border-input max-h-48 rounded-md border [&>[data-slot=scroll-area-viewport]]:max-h-48 [&>[data-slot=scroll-area-viewport]]:space-y-0.5 [&>[data-slot=scroll-area-viewport]]:p-1">
                  {fieldSample.isFetching ? (
                    <p className="text-muted-foreground flex items-center gap-2 px-1.5 py-1 text-xs">
                      <Spinner className="size-3" /> Loading sample data…
                    </p>
                  ) : null}
                  {!fieldSample.isFetching && fieldSample.data && !fieldSample.data.found ? (
                    <p className="text-muted-foreground px-1.5 py-1 text-xs">
                      No submission yet for this vertical/type - showing field names only.
                    </p>
                  ) : null}
                  {SHEET_COLUMN_OPTIONS.map((key) => {
                    const value = fieldSample.data?.values[key];
                    const alreadyAdded = parseSheetColumns(form.sheet_columns).includes(key);
                    // These two render at enqueue time (see SubmissionEventStore), before the delivery
                    // this row is even for has happened - always "in hopper" / blank in the actual
                    // sheet, never whatever this preview's sample submission (already delivered, since
                    // it's the most recent one) happens to show here.
                    const isStaleAtSendTime =
                      key === 'action_status' || key === 'sent_to_client_at';
                    return (
                      <Button
                        key={key}
                        variant="ghost"
                        size="xs"
                        disabled={alreadyAdded}
                        className="h-auto w-full justify-start gap-2 px-1.5 py-1 text-left font-normal"
                        onClick={() => {
                          addColumn(key);
                        }}>
                        <span className="text-foreground w-40 flex-none truncate font-mono font-medium">
                          {key}
                        </span>
                        <span className="text-muted-foreground flex-1 truncate">
                          {isStaleAtSendTime ? (
                            <span className="italic">
                              always &quot;in hopper&quot; / blank when sent
                            </span>
                          ) : (
                            value || <span className="italic">empty</span>
                          )}
                        </span>
                        {!alreadyAdded && <Plus className="text-muted-foreground flex-none" />}
                      </Button>
                    );
                  })}
                </ScrollArea>
                <FieldDescription>
                  Values from the most recent submission for this vertical/type. Click a field to
                  add it to the columns above.
                </FieldDescription>
              </FormField>

              <FormField label="Free Text Column" htmlFor="hopper-free-text-column">
                <div className="flex gap-2">
                  <Input
                    id="hopper-free-text-column"
                    value={freeTextColumn}
                    placeholder='A fixed value, e.g. PENDING... or leave blank for ""'
                    onChange={(e) => {
                      setFreeTextColumn(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFreeTextColumn();
                      }
                    }}
                  />
                  <Button className="flex-none" onClick={addFreeTextColumn}>
                    Add
                  </Button>
                </div>
                <FieldDescription>
                  Adds a fixed value column, same as every field above but constant for every row -
                  not read from the submission. You can also type one directly into
                  &quot;Columns&quot; above wrapped in double quotes.
                </FieldDescription>
              </FormField>
            </>
          ) : (
            <>
              <FormField label="OAuth Refresh Token" htmlFor="hopper-youtube-refresh-token">
                <Input
                  id="hopper-youtube-refresh-token"
                  type="password"
                  className="font-mono"
                  value={form.youtube_refresh_token}
                  placeholder={
                    form.youtube_refresh_token_was_set
                      ? '•••• already set - leave blank to keep it'
                      : 'Paste the refresh token for the reporting account'
                  }
                  onChange={(e) => {
                    set('youtube_refresh_token', e.target.value);
                    if (e.target.value !== '') {
                      set('youtube_clear_refresh_token', false);
                    }
                  }}
                />
                {form.youtube_refresh_token_was_set && !form.youtube_clear_refresh_token ? (
                  <Button
                    variant="link"
                    size="xs"
                    className="text-destructive-ink h-auto w-fit px-0"
                    onClick={() => {
                      set('youtube_clear_refresh_token', true);
                      set('youtube_refresh_token', '');
                    }}>
                    Clear saved token
                  </Button>
                ) : null}
                {form.youtube_clear_refresh_token ? (
                  <p className="text-destructive-ink text-xs">Token will be cleared on save.</p>
                ) : null}
              </FormField>

              {form.submission_type === 'Content' ? (
                <>
                  <FormField label="Reason" htmlFor="hopper-youtube-reason">
                    <Input
                      id="hopper-youtube-reason"
                      className="font-mono"
                      value={form.youtube_reason_id}
                      list="hopper-youtube-reason-options"
                      placeholder="Reason id, e.g. reason-1"
                      onChange={(e) => {
                        set('youtube_reason_id', e.target.value);
                      }}
                    />
                    <datalist id="hopper-youtube-reason-options">
                      {youtubeReasons.data?.results.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label ?? r.id}
                        </option>
                      ))}
                    </datalist>
                  </FormField>

                  <FormField
                    label="Secondary Reason (optional)"
                    htmlFor="hopper-youtube-secondary-reason">
                    <Input
                      id="hopper-youtube-secondary-reason"
                      className="font-mono"
                      value={form.youtube_secondary_reason_id}
                      list="hopper-youtube-secondary-reason-options"
                      placeholder="Secondary reason id"
                      onChange={(e) => {
                        set('youtube_secondary_reason_id', e.target.value);
                      }}
                    />
                    <datalist id="hopper-youtube-secondary-reason-options">
                      {youtubeReasons.data?.results
                        .find((r) => r.id === form.youtube_reason_id)
                        ?.secondary_reasons.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label ?? r.id}
                          </option>
                        ))}
                    </datalist>
                  </FormField>

                  {subCategories.length > 0 || orphanedSubCategories.length > 0 ? (
                    <FormField label="Secondary Reason by Sub-category">
                      <div className="space-y-2">
                        <FieldDescription>
                          A submission whose risk sits under one of these is reported with its
                          reason instead of the default above. Leave a row blank to use the default.
                        </FieldDescription>
                        {subCategories.map((c) => (
                          <div key={c.id} className="flex items-center gap-3">
                            <span
                              className="w-48 shrink-0 truncate text-xs font-medium"
                              title={c.name}>
                              {c.display_name || c.name}
                            </span>
                            <Input
                              aria-label={`Secondary reason for ${c.name}`}
                              className="font-mono"
                              value={form.youtube_secondary_overrides[c.name] ?? ''}
                              list="hopper-youtube-secondary-reason-options"
                              placeholder="Uses default"
                              onChange={(e) => {
                                setSubCategoryReason(c.name, e.target.value);
                              }}
                            />
                          </div>
                        ))}
                        {orphanedSubCategories.map((name) => (
                          <div key={name} className="flex items-center gap-3">
                            <span
                              className="text-destructive-ink w-48 shrink-0 truncate text-xs font-medium"
                              title={`${name} - no longer in the taxonomy under this policy violation`}>
                              {name}
                            </span>
                            <Input
                              readOnly
                              aria-label={`Secondary reason for ${name}`}
                              className="bg-muted text-muted-foreground font-mono"
                              value={form.youtube_secondary_overrides[name] ?? ''}
                            />
                            <Button
                              variant="link"
                              size="xs"
                              className="text-destructive-ink h-auto shrink-0 px-0"
                              onClick={() => {
                                removeSubCategoryReason(name);
                              }}>
                              No longer in taxonomy - remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </FormField>
                  ) : null}
                </>
              ) : (
                <FormField label="Abuse Type" htmlFor="hopper-youtube-abuse-type">
                  <Input
                    id="hopper-youtube-abuse-type"
                    className="font-mono"
                    value={form.youtube_abuse_type}
                    placeholder="Abuse type, e.g. abuse-1"
                    onChange={(e) => {
                      set('youtube_abuse_type', e.target.value);
                    }}
                  />
                </FormField>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Comments (optional)" htmlFor="hopper-youtube-comments">
                  <Input
                    id="hopper-youtube-comments"
                    value={form.youtube_comments}
                    onChange={(e) => {
                      set('youtube_comments', e.target.value);
                    }}
                  />
                </FormField>
                <FormField label="Language (optional)" htmlFor="hopper-youtube-language">
                  <Input
                    id="hopper-youtube-language"
                    className="font-mono"
                    value={form.youtube_language}
                    placeholder="en"
                    onChange={(e) => {
                      set('youtube_language', e.target.value);
                    }}
                  />
                </FormField>
              </div>
            </>
          )}

          <FormField label="Internal Sheet" htmlFor="hopper-internal-sheet-enabled">
            <div className="flex items-start gap-2">
              <Switch
                id="hopper-internal-sheet-enabled"
                className="mt-0.5"
                checked={form.internal_sheet_enabled}
                onCheckedChange={() => {
                  set('internal_sheet_enabled', !form.internal_sheet_enabled);
                }}
              />
              <FieldDescription>
                Also write every delivery to an internal Google Sheet, with its own columns. Best
                effort: if the internal write fails, the submission still counts as delivered.
              </FieldDescription>
            </div>
          </FormField>

          {form.internal_sheet_enabled ? (
            <>
              <FormField label="Internal Spreadsheet ID" htmlFor="hopper-internal-spreadsheet-id">
                <Input
                  id="hopper-internal-spreadsheet-id"
                  className="font-mono"
                  value={form.internal_spreadsheet_id}
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                  onChange={(e) => {
                    set('internal_spreadsheet_id', e.target.value);
                  }}
                />
                <FieldDescription>
                  Share this sheet with{' '}
                  <code className="bg-muted px-1 py-0.5 break-all">
                    {sheetAccount.data?.email ??
                      'pri-delivery-gateway@<project>.iam.gserviceaccount.com'}
                  </code>{' '}
                  as Editor too.
                </FieldDescription>
              </FormField>

              <FormField label="Internal Sheet Tab (optional)" htmlFor="hopper-internal-sheet-tab">
                <Input
                  id="hopper-internal-sheet-tab"
                  className="font-mono"
                  value={form.internal_sheet_tab}
                  placeholder="Leave blank for the first sheet"
                  onChange={(e) => {
                    set('internal_sheet_tab', e.target.value);
                  }}
                />
              </FormField>

              <FormField
                label="Internal Columns (in order)"
                htmlFor="hopper-internal-sheet-columns">
                <Input
                  id="hopper-internal-sheet-columns"
                  className="font-mono"
                  value={form.internal_sheet_columns}
                  list="hopper-internal-sheet-column-options"
                  placeholder="id, content_id, display_name, ..."
                  onChange={(e) => {
                    set('internal_sheet_columns', e.target.value);
                  }}
                />
                <datalist id="hopper-internal-sheet-column-options">
                  {SHEET_COLUMN_OPTIONS.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <FieldDescription>
                  Same field names as a Google Sheet delivery. Wrap fixed text in double quotes.
                  Must include id or item_external_id.
                </FieldDescription>
              </FormField>
            </>
          ) : null}

          <FieldSet>
            <FieldLegend>Delivery Targets</FieldLegend>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['daily_target', 'Daily'],
                  ['weekly_target', 'Weekly'],
                  ['monthly_target', 'Monthly']
                ] as const
              ).map(([key, label]) => (
                <Field key={key}>
                  <FieldLabel htmlFor={`hopper-${key}`}>{label}</FieldLabel>
                  <Input
                    id={`hopper-${key}`}
                    type="number"
                    min={0}
                    value={form[key]}
                    onChange={(e) => {
                      const value = Number.parseInt(e.target.value, 10) || 0;
                      if (key === 'weekly_target') {
                        setWeeklyTouched(true);
                        set('weekly_target', value);
                        return;
                      }

                      if (key === 'monthly_target') {
                        setMonthlyTouched(true);
                        set('monthly_target', value);
                        return;
                      }

                      setForm((previous) => ({
                        ...previous,
                        daily_target: value,
                        weekly_target: weeklyTouched
                          ? previous.weekly_target
                          : value * WEEKLY_PER_DAILY,
                        monthly_target: monthlyTouched
                          ? previous.monthly_target
                          : value * MONTHLY_PER_DAILY
                      }));
                    }}
                  />
                </Field>
              ))}
            </div>
            <FieldDescription>
              Typing a daily target fills the other two as a starting point; edit either and it
              stays as you set it. Zero means no target - the dashboard draws no bar. The
              dashboard&apos;s own daily/weekly/monthly toggle picks which of these it measures
              against.
            </FieldDescription>
          </FieldSet>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Batch Capacity" htmlFor="hopper-batch-size">
              <Input
                id="hopper-batch-size"
                type="number"
                min={1}
                value={form.batch_size}
                onChange={(e) => {
                  set('batch_size', Number.parseInt(e.target.value, 10) || 50);
                }}
              />
              <FieldDescription>
                Rows sent per fire, not a delivery target - see Delivery Targets above.
              </FieldDescription>
            </FormField>
            <FormField label="Active State" htmlFor="hopper-enabled">
              <div className="flex h-8 items-center">
                <Switch
                  id="hopper-enabled"
                  checked={form.enabled}
                  onCheckedChange={() => {
                    set('enabled', !form.enabled);
                  }}
                />
              </div>
            </FormField>
          </div>

          <FormField label="Test Mode" htmlFor="hopper-test-mode">
            <div className="flex items-start gap-2">
              <Switch
                id="hopper-test-mode"
                className="mt-0.5"
                checked={form.test_mode}
                onCheckedChange={() => {
                  set('test_mode', !form.test_mode);
                }}
              />
              <FieldDescription>
                {form.target_type === 'youtube'
                  ? 'Runs the liveness check and reason validation against YouTube for real, but stops before filing the report. Applies immediately, including to deliveries already queued. Reports are only ever filed on a live environment: anywhere else this behaves as if test mode were on, whatever it is set to. Nothing downstream of the send runs: the submission is not marked delivered and is not relayed or audited - it stays in the hopper and sends for real once this is off. Each is tested once, and editing this hopper tests them again.'
                  : 'Captures deliveries in the Test Inbox instead of sending them. Applies to new deliveries only — anything already queued keeps the setting it was queued with. Everything downstream of the send still runs: the submission is marked delivered, and it is relayed and audited exactly as a real one.'}
              </FieldDescription>
            </div>
          </FormField>

          {form.test_mode && form.target_type === 'webhook' ? (
            <FormField label="Simulated Response Status" htmlFor="hopper-test-status">
              <Input
                id="hopper-test-status"
                type="number"
                className="w-24"
                value={form.test_simulated_status_code}
                placeholder="200"
                onChange={(e) => {
                  set('test_simulated_status_code', e.target.value);
                }}
              />
            </FormField>
          ) : null}

          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
        </DialogBody>

        <DialogFooter sticky>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              pending ||
              form.name === '' ||
              form.policy_violation === '' ||
              (form.mode === 'scheduled' && form.schedule === '') ||
              (form.target_type === 'sheet' &&
                (form.spreadsheet_id.trim() === '' ||
                  parseSheetColumns(form.sheet_columns).length === 0)) ||
              (form.internal_sheet_enabled &&
                (form.internal_spreadsheet_id.trim() === '' ||
                  parseSheetColumns(form.internal_sheet_columns).length === 0)) ||
              (form.target_type === 'youtube' &&
                ((form.youtube_refresh_token.trim() === '' &&
                  (!form.youtube_refresh_token_was_set || form.youtube_clear_refresh_token)) ||
                  (form.submission_type === 'Content'
                    ? form.youtube_reason_id.trim() === ''
                    : form.youtube_abuse_type.trim() === '')))
            }
            onClick={submit}>
            {pending ? <Spinner /> : <Check />}
            {pending ? 'Saving Configuration...' : 'Save Delivery'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// A labelled form row on the package's Field. The label is associated through `htmlFor` where the
// control is a single input; a group of controls (toggle group, sub-category rows) leaves it off.
function FormField({
  label,
  htmlFor,
  children
}: {
  readonly label: string;
  readonly htmlFor?: string;
  readonly children: ReactNode;
}) {
  return (
    <Field>
      <FieldLabel {...(htmlFor ? {htmlFor} : {})}>{label}</FieldLabel>
      {children}
    </Field>
  );
}

// Whether this hopper resolves to a real delivery address. Mirrors what "Fire now" needs: a webhook
// URL, a spreadsheet id, or a saved Pioneer API token.
function hasDeliveryTarget(config: HopperConfig): boolean {
  return Boolean(
    config.target_type === 'sheet'
      ? config.spreadsheet_id
      : config.target_type === 'youtube'
        ? config.youtube_refresh_token_set
        : config.webhook_url
  );
}

function targetLabel(config: HopperConfig): string {
  return config.target_type === 'sheet'
    ? 'Google Sheet'
    : config.target_type === 'youtube'
      ? 'Pioneer API'
      : 'Webhook';
}

function targetAddress(config: HopperConfig): string {
  return config.target_type === 'sheet'
    ? `${config.spreadsheet_id ?? ''}${config.sheet_tab ? `/${config.sheet_tab}` : ''}`
    : config.target_type === 'youtube'
      ? config.youtube_refresh_token_set
        ? 'Reporting account token saved'
        : ''
      : (config.webhook_url ?? '');
}

function HopperCard({
  config,
  onEdit,
  onDelete,
  onToggle,
  onToggleTestMode,
  onFire
}: {
  readonly config: HopperConfig;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onToggle: () => void;
  readonly onToggleTestMode: () => void;
  readonly onFire: () => Promise<{sent: number; video_ids: string[]}>;
}) {
  const [firing, setFiring] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const actions = useHopperDeliveryActions(config.id, showInbox);
  const clearActionsMut = useClearHopperDeliveryActions();
  const retryMut = useRetrySubmissionDelivery();
  const resendMut = useResendDeliveryAction();

  async function handleFire() {
    setFiring(true);
    try {
      const result = await onFire();
      showToast(result.sent === 0 ? 'No items to send' : `Sent ${result.sent} items`);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Execution failed'), 'error');
    } finally {
      setFiring(false);
    }
  }

  async function handleRetryAction(action: {
    readonly id: string;
    readonly origin: string | null;
    readonly stream_id: string | null;
  }) {
    try {
      if (action.origin === 'p0-slack') {
        await resendMut.mutateAsync(action.id);
      } else if (action.stream_id) {
        await retryMut.mutateAsync(action.stream_id);
      } else {
        return;
      }

      showToast('Delivery retry queued.');
    } catch (error_) {
      showToast(getApiErrorMessage(error_, 'Failed to retry delivery.'), 'error');
    }
  }

  const deliverable = hasDeliveryTarget(config);
  const address = targetAddress(config);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span className="truncate">{config.name}</span>
          <Badge variant="outline">{config.submission_type}</Badge>
          {/* A disabled hopper is named rather than dimmed: a faded card still has to be read, and
              the switch alone does not say why nothing has fired. */}
          {config.enabled ? null : <Badge variant="outline">Disabled</Badge>}
        </CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-x-2">
          <span className="text-foreground font-medium">{config.policy_violation}</span>
          <span aria-hidden="true">·</span>
          <span>{targetLabel(config)}</span>
          {address ? (
            <span className="truncate font-mono" title={address}>
              {address}
            </span>
          ) : null}
        </CardDescription>
        <CardAction className="flex items-center gap-3">
          <Field orientation="horizontal" className="w-auto gap-1.5">
            <Switch
              id={`hopper-test-mode-${config.id}`}
              size="sm"
              checked={config.test_mode}
              aria-label={`Test mode for ${config.name}`}
              onCheckedChange={onToggleTestMode}
            />
            <FieldLabel
              htmlFor={`hopper-test-mode-${config.id}`}
              className="text-muted-foreground font-normal">
              Test
            </FieldLabel>
          </Field>
          <Switch
            checked={config.enabled}
            aria-label={`Enable ${config.name}`}
            onCheckedChange={onToggle}
          />
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline">
            {config.schedule ? <Clock /> : <Zap />}
            {config.schedule ? 'Scheduled' : 'Direct send'}
          </Badge>
          {config.schedule ? (
            <Badge variant="outline" className="font-mono">
              {config.schedule}
            </Badge>
          ) : null}
          <Badge variant="outline">{config.batch_size} items / batch</Badge>
          {config.test_mode ? <Badge variant="outline">Test mode</Badge> : null}
          {config.internal_sheet_enabled ? <Badge variant="outline">Internal sheet</Badge> : null}
        </div>

        <p
          className={cn('text-xs', deliverable ? 'text-muted-foreground' : 'text-destructive-ink')}>
          {deliverable
            ? config.last_fired_at
              ? `Last fired ${formatDistanceToNow(new Date(config.last_fired_at), {addSuffix: true})}`
              : 'Ready — not fired yet'
            : 'No delivery target configured — nothing can be sent until one is set'}
        </p>

        {/* The 1px gaps show the --border fill through, so the three tiles read as one ruled
            strip without each needing a border of its own. */}
        <div className="bg-border border-border grid grid-cols-3 gap-px rounded-lg border">
          {(
            [
              ['Daily', config.daily_target],
              ['Weekly', config.weekly_target],
              ['Monthly', config.monthly_target]
            ] as const
          ).map(([label, value]) => (
            <Item key={label} size="xs" className="bg-card px-3 py-2">
              <ItemContent>
                <ItemDescription>{label} target</ItemDescription>
                <ItemTitle className="text-sm font-semibold tabular-nums">
                  {value > 0 ? value : '—'}
                </ItemTitle>
              </ItemContent>
            </Item>
          ))}
        </div>

        {showInbox ? (
          <DeliveryActionsPanel
            actions={actions.data}
            isFetching={actions.isFetching}
            clearing={clearActionsMut.isPending}
            retrying={retryMut.isPending || resendMut.isPending}
            onClear={() => {
              clearActionsMut.mutate(config.id, {
                onError(error_) {
                  showToast(getApiErrorMessage(error_, 'Failed to clear delivery inbox.'), 'error');
                }
              });
            }}
            onRetry={(action) => {
              void handleRetryAction(action);
            }}
          />
        ) : null}
      </CardContent>

      <CardFooter className="flex flex-wrap items-center gap-2">
        {deliverable ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button variant="outline" size="sm" disabled={firing} onClick={handleFire} />
              }>
              {firing ? <Spinner /> : <Play />}
              Fire now
            </TooltipTrigger>
            <TooltipContent>Trigger a batch run for this delivery target</TooltipContent>
          </Tooltip>
        ) : null}
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil />
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={showInbox}
          onClick={() => {
            setShowInbox(!showInbox);
          }}>
          <Inbox />
          Delivery actions
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          className="text-destructive-ink ml-auto"
          aria-label={`Delete ${config.name}`}
          onClick={onDelete}>
          <Trash2 />
        </Button>
      </CardFooter>
    </Card>
  );
}

// The admin config panels read as a Card while they summarise and as a plain bordered section on
// the page background while they edit: a form does not live inside a Card, but the read-only
// summary still does. Both modes share one header so the title and description are written once.
function ConfigPanel({
  editing = false,
  icon,
  title,
  description,
  action,
  children
}: {
  readonly editing?: boolean;
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  if (editing) {
    return (
      <section className="border-border bg-card space-y-4 rounded-lg border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="font-heading flex items-center gap-2 text-sm font-medium">
              {icon}
              {title}
            </h3>
            <p className="text-muted-foreground text-xs/relaxed">{description}</p>
          </div>
          {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </div>
        {children}
      </section>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
        {action ? <CardAction className="flex items-center gap-2">{action}</CardAction> : null}
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

const P0_SLACK_CHANNEL_FIELDS = [
  {
    key: 'pendingWebhookUrl',
    dataKey: 'pending_webhook_url',
    label: 'Pending QC Slack channel',
    help: 'Posts a P0 requiring review, and its rejection.'
  },
  {
    key: 'deliveryWebhookUrl',
    dataKey: 'delivery_webhook_url',
    label: 'Delivery notification Slack channel',
    help: 'Posts once a P0 is approved or amended and sent.'
  }
] as const;

function P0SlackAlertConfigCard() {
  const {data} = useP0SlackAlertConfig();
  const updateMut = useUpdateP0SlackAlertConfig();
  const [editing, setEditing] = useState(false);
  const [pendingWebhookUrl, setPendingWebhookUrl] = useState('');
  const [deliveryWebhookUrl, setDeliveryWebhookUrl] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [testSimulatedStatusCode, setTestSimulatedStatusCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showInbox, setShowInbox] = useState(false);
  const actions = useP0SlackDeliveryActions(showInbox);
  const clearActionsMut = useClearP0SlackDeliveryActions();
  const resendMut = useResendDeliveryAction();

  function startEdit() {
    setError(null);
    setPendingWebhookUrl(data?.pending_webhook_url ?? '');
    setDeliveryWebhookUrl(data?.delivery_webhook_url ?? '');
    setTestMode(data?.test_mode ?? false);
    setTestSimulatedStatusCode(data?.test_simulated_status_code?.toString() ?? '');
    setEditing(true);
  }

  async function handleSave() {
    setError(null);
    try {
      await updateMut.mutateAsync({
        pendingWebhookUrl: pendingWebhookUrl.trim() || null,
        deliveryWebhookUrl: deliveryWebhookUrl.trim() || null,
        // The escalation channel is edited from the Potential P0 Escalation Timer card, since that's
        // the config it belongs to - carry the current value through unchanged rather than clobbering
        // it with a value this card never showed.
        escalationWebhookUrl: data?.escalation_webhook_url ?? null,
        testMode,
        testSimulatedStatusCode: testSimulatedStatusCode.trim()
          ? Number(testSimulatedStatusCode)
          : null
      });
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save P0 Slack alert webhook URLs.'));
    }
  }

  const headerAction = (
    <>
      {data?.test_mode_allowed ? (
        <Field orientation="horizontal" className="w-auto gap-1.5">
          <Switch
            id="p0-slack-header-test-mode"
            size="sm"
            checked={data.test_mode}
            aria-label="Test mode for P0 Slack alerts"
            onCheckedChange={() => {
              updateMut.mutate(
                {
                  pendingWebhookUrl: data.pending_webhook_url,
                  deliveryWebhookUrl: data.delivery_webhook_url,
                  escalationWebhookUrl: data.escalation_webhook_url,
                  testMode: !data.test_mode,
                  testSimulatedStatusCode: data.test_simulated_status_code
                },
                {
                  onError(error_) {
                    showToast(getApiErrorMessage(error_, 'Failed to toggle test mode.'), 'error');
                  }
                }
              );
            }}
          />
          <FieldContent>
            <FieldLabel htmlFor="p0-slack-header-test-mode" className="text-muted-foreground">
              Test
            </FieldLabel>
            <FieldDescription>Captures instead of posting to Slack.</FieldDescription>
          </FieldContent>
        </Field>
      ) : null}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-expanded={showInbox}
              aria-label="Show delivery actions"
              onClick={() => {
                setShowInbox(!showInbox);
              }}
            />
          }>
          <Inbox />
        </TooltipTrigger>
        <TooltipContent>Show delivery actions</TooltipContent>
      </Tooltip>
      {editing ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Edit P0 Slack alert webhooks"
          onClick={startEdit}>
          <Pencil />
        </Button>
      )}
    </>
  );

  return (
    <ConfigPanel
      editing={editing}
      icon={<MessageSquare className="text-destructive-ink size-4" />}
      title="P0 Slack Alert Webhooks"
      description="Slack Incoming Webhook URLs a P0 alert notifies on for pending review, approval, amendment or rejection."
      action={headerAction}>
      {editing ? (
        <div className="space-y-3">
          {(
            [
              [P0_SLACK_CHANNEL_FIELDS[0], pendingWebhookUrl, setPendingWebhookUrl] as const,
              [P0_SLACK_CHANNEL_FIELDS[1], deliveryWebhookUrl, setDeliveryWebhookUrl] as const
            ] as const
          ).map(([field, fieldValue, setFieldValue]) => (
            <Field key={field.key}>
              <FieldLabel htmlFor={`p0-slack-${field.key}`}>{field.label}</FieldLabel>
              <Input
                id={`p0-slack-${field.key}`}
                className="font-mono"
                value={fieldValue}
                placeholder="https://hooks.slack.com/services/..."
                onChange={(e) => {
                  setFieldValue(e.target.value);
                }}
              />
              <FieldDescription>{field.help}</FieldDescription>
            </Field>
          ))}
          {data?.test_mode_allowed ? (
            <>
              <Field orientation="horizontal">
                <Switch
                  id="p0-slack-test-mode"
                  checked={testMode}
                  onCheckedChange={() => {
                    setTestMode(!testMode);
                  }}
                />
                <FieldLabel htmlFor="p0-slack-test-mode" className="font-normal">
                  Test mode: capture instead of posting to Slack.
                </FieldLabel>
              </Field>
              {testMode ? (
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="p0-slack-test-status">Simulated response status</FieldLabel>
                  <Input
                    id="p0-slack-test-status"
                    type="number"
                    className="w-24"
                    value={testSimulatedStatusCode}
                    placeholder="200"
                    onChange={(e) => {
                      setTestSimulatedStatusCode(e.target.value);
                    }}
                  />
                </Field>
              ) : null}
            </>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={updateMut.isPending}
              onClick={() => {
                setEditing(false);
              }}>
              Cancel
            </Button>
            <Button size="sm" disabled={updateMut.isPending} onClick={() => void handleSave()}>
              {updateMut.isPending ? <Spinner /> : <Check />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <ItemGroup>
          {P0_SLACK_CHANNEL_FIELDS.map((field) => (
            <Item key={field.key} size="xs" className="px-0">
              <ItemContent>
                <ItemTitle>{field.label}</ItemTitle>
                <ItemDescription className="truncate font-mono">
                  {data?.[field.dataKey] ?? 'Not configured'}
                </ItemDescription>
              </ItemContent>
            </Item>
          ))}
        </ItemGroup>
      )}

      {showInbox ? (
        <DeliveryActionsPanel
          actions={actions.data}
          isFetching={actions.isFetching}
          clearing={clearActionsMut.isPending}
          retrying={resendMut.isPending}
          onClear={() => {
            clearActionsMut.mutate(undefined, {
              onError(error_) {
                showToast(getApiErrorMessage(error_, 'Failed to clear delivery inbox.'), 'error');
              }
            });
          }}
          onRetry={async (action) => {
            try {
              await resendMut.mutateAsync(action.id);
              showToast('Delivery retry queued.');
            } catch (error_) {
              showToast(getApiErrorMessage(error_, 'Failed to resend delivery.'), 'error');
            }
          }}
        />
      ) : null}
    </ConfigPanel>
  );
}

type StatusCheckField = {
  key: keyof UpdateSubmissionStatusCheckConfigPayload;
  label: string;
  help: string;
  unit: string;
  step: number;
  min: number;
};

const STATUS_CHECK_FIELD_GROUPS: Array<{title: string; fields: StatusCheckField[]}> = [
  {
    title: 'Before delivery',
    fields: [
      {
        key: 'pre_delivery_interval_hours',
        label: 'Check every',
        help: "How often to recheck a submission that's still waiting in the hopper, not yet sent to the client.",
        unit: 'hours',
        step: 0.5,
        min: 0.5
      }
    ]
  },
  {
    title: 'Just after delivery',
    fields: [
      {
        key: 'post_delivery_daily_interval_hours',
        label: 'Check every',
        help: 'How often to recheck once the submission has been sent to the client - this is when a takedown is most likely to happen.',
        unit: 'hours',
        step: 0.5,
        min: 0.5
      },
      {
        key: 'post_delivery_daily_duration_days',
        label: 'For the first',
        help: 'How many days after delivery to keep checking at that pace before slowing down.',
        unit: 'days',
        step: 0.5,
        min: 0.5
      }
    ]
  },
  {
    title: 'Long after delivery',
    fields: [
      {
        key: 'post_delivery_weekly_interval_days',
        label: 'Then check every',
        help: 'How often to recheck once the "just after delivery" window above has passed.',
        unit: 'days',
        step: 0.5,
        min: 0.5
      },
      {
        key: 'post_delivery_cutoff_days',
        label: 'Stop checking after',
        help: 'How long after delivery to keep rechecking at all - past this, the submission drops out of the rotation completely.',
        unit: 'days',
        step: 1,
        min: 1
      }
    ]
  },
  {
    title: 'Removed submissions',
    fields: [
      {
        key: 'stop_after_consecutive_removed',
        label: 'Stop after',
        help: 'Stop rechecking a submission once this many checks in a row have found it removed. A status check asked for from the Submissions page still goes out. 0 never stops.',
        unit: 'removed results in a row',
        step: 1,
        min: 0
      }
    ]
  },
  {
    title: 'Batch size',
    fields: [
      {
        key: 'batch_size',
        label: 'Up to',
        help: 'The runner ticks roughly once a minute; this caps how many due submissions it rechecks per tick so a large backlog can’t overload Scrapy all at once.',
        unit: 'submissions per run',
        step: 1,
        min: 1
      }
    ]
  }
];

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/// Plain-language summary of the same cadence numbers the form edits - this is what an admin
/// actually wants to know ("how often are we checking, and for how long"), not the raw field names.
function describeCadence(config: {
  pre_delivery_interval_hours: number;
  post_delivery_daily_interval_hours: number;
  post_delivery_daily_duration_days: number;
  post_delivery_weekly_interval_days: number;
  post_delivery_cutoff_days: number;
  stop_after_consecutive_removed: number;
  batch_size: number;
}): string[] {
  return [
    `Before it's sent to the client: rechecked every ${formatNumber(config.pre_delivery_interval_hours)}h.`,
    `For the first ${formatNumber(config.post_delivery_daily_duration_days)} days after delivery: rechecked every ${formatNumber(config.post_delivery_daily_interval_hours)}h.`,
    `After that: rechecked every ${formatNumber(config.post_delivery_weekly_interval_days)} days, until ${formatNumber(config.post_delivery_cutoff_days)} days after delivery - then checking stops.`,
    config.stop_after_consecutive_removed > 0
      ? `Once ${formatNumber(config.stop_after_consecutive_removed)} checks in a row find it removed: no longer rechecked.`
      : 'Removed submissions keep being rechecked.',
    'Each recheck also re-fetches the thumbnail, to catch the creator swapping the image.',
    `Up to ${formatNumber(config.batch_size)} submissions rechecked per run (about once a minute).`
  ];
}

function ApolloRelayConfigCard() {
  const {data} = useApolloRelayConfig();
  const updateMut = useUpdateApolloRelayConfig();
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    if (!data) {
      return;
    }

    setError(null);
    try {
      await updateMut.mutateAsync({enabled: !data.enabled});
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to change the Apollo setting.'));
    }
  }

  // Shown next to the switch rather than left to the reader: the switch alone cannot tell you whether
  // flipping it will change anything, because an environment with no topic sends nothing either way.
  const state = data?.sending
    ? {label: 'Sending', tone: 'success' as const}
    : data?.enabled === false
      ? {label: 'Switched off', tone: null}
      : {label: 'Not sending — no topic configured here', tone: 'destructive' as const};

  return (
    <ConfigPanel
      icon={<Power className="text-muted-foreground size-4" />}
      title="Send to Apollo"
      description="Publishes every submission delivered to a client onward to Apollo, and enables the per-submission Send to Apollo action. Switching it off stops both immediately — nothing queues up while it is off, so switching it back on covers new deliveries only."
      action={
        <Button
          variant={data?.enabled ? 'default' : 'outline'}
          size="sm"
          disabled={!data || updateMut.isPending}
          aria-pressed={data?.enabled ?? false}
          onClick={() => void toggle()}>
          <Power />
          {data?.enabled ? 'Enabled' : 'Disabled'}
        </Button>
      }>
      <ItemGroup>
        <Item size="xs" className="px-0">
          <ItemContent>
            <ItemTitle>Status</ItemTitle>
            <ItemDescription>
              <Badge {...(state.tone ? toneBadge(state.tone) : {variant: 'outline'})}>
                {state.label}
              </Badge>
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item size="xs" className="px-0">
          <ItemContent>
            <ItemTitle>Topic</ItemTitle>
            <ItemDescription className="text-foreground font-mono break-all">
              {data?.topic ?? (
                <span className="text-muted-foreground font-sans">Not configured</span>
              )}
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item size="xs" className="px-0">
          <ItemContent>
            <ItemTitle>Affiliate ID</ItemTitle>
            <ItemDescription className="text-foreground font-mono break-all">
              {data?.affiliate_id ?? (
                <span className="text-muted-foreground font-sans">
                  Not set — messages omit AffiliateId
                </span>
              )}
            </ItemDescription>
          </ItemContent>
        </Item>
        {data?.modified_by ? (
          <Item size="xs" className="px-0">
            <ItemContent>
              <ItemTitle>Last changed</ItemTitle>
              <ItemDescription>
                {data.modified_by}
                {data.modified_at
                  ? ` — ${formatDistanceToNow(new Date(data.modified_at), {addSuffix: true})}`
                  : ''}
              </ItemDescription>
            </ItemContent>
          </Item>
        ) : null}
      </ItemGroup>

      {error ? (
        <Alert variant="destructive" className="bg-card">
          <AlertCircle />
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      ) : null}
    </ConfigPanel>
  );
}

function SubmissionStatusCheckConfigCard() {
  const {data} = useSubmissionStatusCheckConfig();
  const updateMut = useUpdateSubmissionStatusCheckConfig();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateSubmissionStatusCheckConfigPayload>({
    batch_size: 50,
    pre_delivery_interval_hours: 6,
    post_delivery_daily_interval_hours: 24,
    post_delivery_daily_duration_days: 7,
    post_delivery_weekly_interval_days: 7,
    post_delivery_cutoff_days: 30,
    stop_after_consecutive_removed: 3
  });
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setError(null);
    if (data) {
      setForm({
        batch_size: data.batch_size,
        pre_delivery_interval_hours: data.pre_delivery_interval_hours,
        post_delivery_daily_interval_hours: data.post_delivery_daily_interval_hours,
        post_delivery_daily_duration_days: data.post_delivery_daily_duration_days,
        post_delivery_weekly_interval_days: data.post_delivery_weekly_interval_days,
        post_delivery_cutoff_days: data.post_delivery_cutoff_days,
        stop_after_consecutive_removed: data.stop_after_consecutive_removed
      });
    }

    setEditing(true);
  }

  async function handleSave() {
    setError(null);
    try {
      await updateMut.mutateAsync(form);
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save status-check settings.'));
    }
  }

  return (
    <ConfigPanel
      editing={editing}
      icon={<Clock className="text-muted-foreground size-4" />}
      title="Submission Status Check Cadence"
      description="How often we recheck a submission on the platform to see if it's still up, before and after it's delivered to the client."
      action={
        editing ? undefined : (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Edit status check cadence"
            onClick={startEdit}>
            <Pencil />
          </Button>
        )
      }>
      {editing ? (
        <div className="space-y-4">
          {STATUS_CHECK_FIELD_GROUPS.map((group) => (
            <FieldSet key={group.title} className="gap-2">
              <FieldLegend>{group.title}</FieldLegend>
              {group.fields.map((field) => (
                <Field key={field.key}>
                  <div className="flex items-center gap-2">
                    <FieldLabel htmlFor={`status-check-${field.key}`} className="w-24 shrink-0">
                      {field.label}
                    </FieldLabel>
                    <Input
                      id={`status-check-${field.key}`}
                      type="number"
                      min={field.min}
                      step={field.step}
                      className="w-24 font-mono"
                      value={form[field.key]}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setForm((prev) => ({
                          ...prev,
                          [field.key]: Number.isFinite(next) ? next : prev[field.key]
                        }));
                      }}
                    />
                    <span className="text-muted-foreground text-xs">{field.unit}</span>
                  </div>
                  <FieldDescription className="pl-[104px]">{field.help}</FieldDescription>
                </Field>
              ))}
            </FieldSet>
          ))}
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={updateMut.isPending}
              onClick={() => {
                setEditing(false);
              }}>
              Cancel
            </Button>
            <Button size="sm" disabled={updateMut.isPending} onClick={() => void handleSave()}>
              {updateMut.isPending ? <Spinner /> : <Check />}
              Save
            </Button>
          </div>
        </div>
      ) : data ? (
        <ul className="text-foreground list-inside list-disc space-y-1 text-xs">
          {describeCadence(data).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : (
        <Spinner className="text-muted-foreground" />
      )}
    </ConfigPanel>
  );
}

type EscalationField = {
  key: 'threshold_minutes' | 'status_check_interval_minutes';
  label: string;
  help: string;
  unit: string;
};

const ESCALATION_FIELDS: EscalationField[] = [
  {
    key: 'threshold_minutes',
    label: 'Slack reminder after',
    help: 'How long a Potential-P0 submission can go unactioned by the customer before we post a Slack reminder to create and send its P0 email.',
    unit: 'minutes'
  },
  {
    key: 'status_check_interval_minutes',
    label: 'Recheck every',
    help: "How often we recheck a submission's platform status while its P0 alert is still pending - much tighter than the normal cadence above, so the customer's own action is caught inside the window.",
    unit: 'minutes'
  }
];

function P0EscalationConfigCard() {
  const {data} = useP0EscalationConfig();
  const updateMut = useUpdateP0EscalationConfig();
  // The Slack channel this timer's reminder posts to lives on the same P0SlackAlertConfig row as the
  // pending/delivery channels (see P0SlackAlertConfigCard), but is edited here since it's this timer's
  // own concern - not the P0 alert lifecycle the other two channels cover.
  const {data: slackData} = useP0SlackAlertConfig();
  const slackUpdateMut = useUpdateP0SlackAlertConfig();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<UpdateP0EscalationConfigPayload>({
    enabled: true,
    threshold_minutes: 60,
    status_check_interval_minutes: 10
  });
  const [escalationWebhookUrl, setEscalationWebhookUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setError(null);
    if (data) {
      setForm({
        enabled: data.enabled,
        threshold_minutes: data.threshold_minutes,
        status_check_interval_minutes: data.status_check_interval_minutes
      });
    }

    setEscalationWebhookUrl(slackData?.escalation_webhook_url ?? '');
    setEditing(true);
  }

  async function handleSave() {
    setError(null);
    try {
      const saves: Array<Promise<unknown>> = [updateMut.mutateAsync(form)];
      const trimmedEscalationUrl = escalationWebhookUrl.trim();
      // Only touch the shared P0SlackAlertConfig row if its data actually loaded and the escalation
      // URL changed - saving with slackData still undefined (GET in flight or failed, while the
      // Edit button renders regardless) would otherwise send pending/delivery as null and wipe both
      // channels this card never showed.
      if (slackData && trimmedEscalationUrl !== (slackData.escalation_webhook_url ?? '')) {
        saves.push(
          slackUpdateMut.mutateAsync({
            pendingWebhookUrl: slackData.pending_webhook_url,
            deliveryWebhookUrl: slackData.delivery_webhook_url,
            escalationWebhookUrl: trimmedEscalationUrl || null,
            testMode: slackData.test_mode,
            testSimulatedStatusCode: slackData.test_simulated_status_code
          })
        );
      }

      await Promise.all(saves);
      setEditing(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save escalation settings.'));
    }
  }

  return (
    <ConfigPanel
      editing={editing}
      icon={<MailWarning className="text-destructive-ink size-4" />}
      title="Potential P0 Escalation Timer"
      description="Posts a Slack reminder if a Potential-P0 risk submission stays unactioned by the customer past the configured window. No P0 email is ever auto-created or auto-sent - an analyst always creates and sends it by hand, from the Slack link."
      action={
        editing ? undefined : (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Edit P0 escalation timer"
            onClick={startEdit}>
            <Pencil />
          </Button>
        )
      }>
      {editing ? (
        <div className="space-y-4">
          <Field orientation="horizontal">
            <FieldLabel htmlFor="p0-escalation-enabled" className="w-24 shrink-0">
              Enabled
            </FieldLabel>
            <Switch
              id="p0-escalation-enabled"
              checked={form.enabled}
              onCheckedChange={() => {
                setForm((prev) => ({...prev, enabled: !prev.enabled}));
              }}
            />
          </Field>
          {ESCALATION_FIELDS.map((field) => (
            <Field key={field.key}>
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor={`p0-escalation-${field.key}`} className="w-24 shrink-0">
                  {field.label}
                </FieldLabel>
                <Input
                  id={`p0-escalation-${field.key}`}
                  type="number"
                  min={1}
                  step={1}
                  className="w-24 font-mono"
                  value={form[field.key]}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      [field.key]: Number.isFinite(next) ? next : prev[field.key]
                    }));
                  }}
                />
                <span className="text-muted-foreground text-xs">{field.unit}</span>
              </div>
              <FieldDescription className="pl-[104px]">{field.help}</FieldDescription>
            </Field>
          ))}
          <Field>
            <FieldLabel htmlFor="p0-escalation-slack-webhook">
              P0 Escalation Slack channel
            </FieldLabel>
            <Input
              id="p0-escalation-slack-webhook"
              className="font-mono"
              value={escalationWebhookUrl}
              placeholder="https://hooks.slack.com/services/..."
              onChange={(e) => {
                setEscalationWebhookUrl(e.target.value);
              }}
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={updateMut.isPending}
              onClick={() => {
                setEditing(false);
              }}>
              Cancel
            </Button>
            <Button size="sm" disabled={updateMut.isPending} onClick={() => void handleSave()}>
              {updateMut.isPending ? <Spinner /> : <Check />}
              Save
            </Button>
          </div>
        </div>
      ) : data ? (
        <ul className="text-foreground list-inside list-disc space-y-1 text-xs">
          <li>{data.enabled ? 'Enabled' : 'Disabled — no Slack reminder will be posted.'}</li>
          <li>Slack reminder after {formatNumber(data.threshold_minutes)} minutes unactioned.</li>
          <li>
            Rechecked every {formatNumber(data.status_check_interval_minutes)} minutes while
            pending.
          </li>
          <li className="font-mono break-all">
            {slackData?.escalation_webhook_url ?? 'Slack channel not configured.'}
          </li>
        </ul>
      ) : (
        <Spinner className="text-muted-foreground" />
      )}
    </ConfigPanel>
  );
}

export function HopperAdmin({initialConfigs, verticals}: HopperAdminProps) {
  const [configs, setConfigs] = useState<HopperConfig[]>(initialConfigs);
  const [modal, setModal] = useState<'create' | HopperConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {confirm, confirmDialog} = useConfirm();
  const [, startTransition] = useTransition();

  const createMut = useCreateHopperConfig();
  const updateMut = useUpdateHopperConfig();
  const deleteMut = useDeleteHopperConfig();

  async function handleSave(form: FormState) {
    setError(null);
    try {
      const payload = formToPayload(form);
      if (modal === 'create') {
        const created = await createMut.mutateAsync(payload);
        setConfigs((previous) =>
          [...previous, created].sort((a, b) => a.name.localeCompare(b.name))
        );
      } else if (modal) {
        // The shared formToPayload sends [] whenever target_type isn't "sheet" (nothing to parse for a
        // webhook hopper) - but on an UPDATE that's not "no columns", it's "field omitted" to the
        // backend (SheetColumns is only applied when the request actually carries it - see
        // HopperConfigsController.Update). Left as-is, saving a webhook hopper - or switching sheet ->
        // webhook - would silently wipe any sheet mapping, with no way to get it back short of
        // re-entering it after switching back. exactOptionalPropertyTypes forbids assigning the key
        // `undefined` explicitly, so it's omitted via destructuring instead.
        const {sheet_columns, ...payloadRest} = payload;
        const updatePayload: UpdateHopperConfigPayload = {
          ...payloadRest,
          ...(form.target_type === 'sheet' ? {sheet_columns} : {}),
          clear_schedule: form.mode === 'direct',
          // Only relevant while target_type stays "webhook" - switching to "sheet"/"youtube" leaves
          // the old webhook_url in place but inert (see HopperConfigsController.ValidateTarget), so
          // there's nothing to explicitly clear in that case.
          clear_webhook_url: form.target_type === 'webhook' && form.webhook_url.trim() === '',
          // Same shape as clear_webhook_url: a null test_simulated_status_code reads as "leave
          // unchanged" to the backend, so blanking the field has to say so explicitly.
          clear_test_simulated_status_code: form.test_simulated_status_code.trim() === '',
          clear_youtube_refresh_token:
            form.target_type === 'youtube' && form.youtube_clear_refresh_token
        };
        const updated = await updateMut.mutateAsync({id: modal.id, payload: updatePayload});
        setConfigs((previous) => previous.map((c) => (c.id === updated.id ? updated : c)));
      }

      setModal(null);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to save delivery config.'));
    }
  }

  function handleToggle(config: HopperConfig) {
    setError(null);
    startTransition(async () => {
      try {
        const updated = await updateMut.mutateAsync({
          id: config.id,
          payload: {enabled: !config.enabled}
        });
        setConfigs((previous) => previous.map((c) => (c.id === updated.id ? updated : c)));
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to toggle delivery config.'));
      }
    });
  }

  function handleToggleTestMode(config: HopperConfig) {
    setError(null);
    startTransition(async () => {
      try {
        const updated = await updateMut.mutateAsync({
          id: config.id,
          payload: {test_mode: !config.test_mode}
        });
        setConfigs((previous) => previous.map((c) => (c.id === updated.id ? updated : c)));
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to toggle test mode.'));
      }
    });
  }

  async function handleDelete(config: HopperConfig) {
    if (
      !(await confirm({
        title: `Delete "${config.name}"?`,
        description: 'This action cannot be undone.'
      }))
    ) {
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        await deleteMut.mutateAsync(config.id);
        setConfigs((previous) => previous.filter((c) => c.id !== config.id));
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to delete delivery config.'));
      }
    });
  }

  const scheduled = configs.filter((c) => c.schedule);
  const direct = configs.filter((c) => !c.schedule);

  // Enabled hoppers that cannot deliver anywhere: nothing fires, nothing fails, and the dashboard
  // simply never fills. Called out above the list rather than left to be noticed card by card.
  const unconfigured = configs.filter((c) => c.enabled && !hasDeliveryTarget(c));

  return (
    <div className="space-y-6">
      {unconfigured.length > 0 ? (
        <Alert>
          <AlertCircle />
          <AlertTitle>
            {unconfigured.length === 1
              ? '1 enabled delivery has no target configured'
              : `${unconfigured.length} enabled deliveries have no target configured`}
          </AlertTitle>
          <AlertDescription>
            {unconfigured.map((c) => c.name).join(', ')} — nothing can be sent until a webhook URL,
            spreadsheet id or Pioneer API token is set. Edit the delivery to add one.
          </AlertDescription>
        </Alert>
      ) : null}

      <P0SlackAlertConfigCard />
      <ApolloRelayConfigCard />
      <SubmissionStatusCheckConfigCard />
      <P0EscalationConfigCard />

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            <Clock />
            {scheduled.length} Scheduled
          </Badge>
          <Badge variant="secondary">
            <Zap />
            {direct.length} Direct
          </Badge>
        </div>

        <Button
          size="sm"
          onClick={() => {
            setModal('create');
          }}>
          <Plus />
          New Delivery Config
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      ) : null}

      {configs.length === 0 ? (
        <Empty variant="outline">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Layers />
            </EmptyMedia>
            <EmptyTitle>No Delivery Configurations</EmptyTitle>
            <EmptyDescription>
              Click the button above to add a new submission webhook pipeline.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="space-y-6">
          {scheduled.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
                <Clock className="size-4" /> Scheduled Delivery
              </h3>
              {scheduled.map((c) => (
                <HopperCard
                  key={c.id}
                  config={c}
                  onEdit={() => {
                    setModal(c);
                  }}
                  onDelete={() => {
                    void handleDelete(c);
                  }}
                  onToggle={() => {
                    handleToggle(c);
                  }}
                  onToggleTestMode={() => {
                    handleToggleTestMode(c);
                  }}
                  onFire={async () => hopperConfigsApi.fire(c.id)}
                />
              ))}
            </div>
          ) : null}

          {direct.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
                <Zap className="size-4" /> Direct Send Pipelines
              </h3>
              {direct.map((c) => (
                <HopperCard
                  key={c.id}
                  config={c}
                  onEdit={() => {
                    setModal(c);
                  }}
                  onDelete={() => {
                    void handleDelete(c);
                  }}
                  onToggle={() => {
                    handleToggle(c);
                  }}
                  onToggleTestMode={() => {
                    handleToggleTestMode(c);
                  }}
                  onFire={async () => hopperConfigsApi.fire(c.id)}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}

      {modal ? (
        <HopperModal
          initial={modal === 'create' ? blankForm(verticals) : configToForm(modal)}
          verticals={verticals}
          onSave={handleSave}
          onClose={() => {
            setModal(null);
          }}
        />
      ) : null}
      {confirmDialog}
    </div>
  );
}
